import { isEmpty, isNil } from 'lodash';
import {
  Grid,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import { listVendorsByPage, updateVendorSort } from 'store/thunks/vendorManagementThunk';

const PRIMARY_DARK = '#021544';
const COLUMN_COUNT = 3;

const headCellSx = {
  color: '#fff',
  fontWeight: 700,
};

const SORT_ENUM = {
  vendor: { asc: 'VENDOR_ASC', desc: 'VENDOR_DESC' },
  createdAt: { asc: 'CREATED_AT_ASC', desc: 'CREATED_AT_DESC' },
};

const SORT_STATE = {
  VENDOR_ASC: { orderBy: 'vendor', order: 'asc' },
  VENDOR_DESC: { orderBy: 'vendor', order: 'desc' },
  CREATED_AT_ASC: { orderBy: 'createdAt', order: 'asc' },
  CREATED_AT_DESC: { orderBy: 'createdAt', order: 'desc' },
};

const COLUMNS = [
  { id: 'vendor', label: 'Vendor Name', sortable: true },
  { id: 'vendorId', label: 'Vendor ID', sortable: false },
  { id: 'createdAt', label: 'Created At', sortable: true },
];

const VendorManagementTable = () => {
  const dispatch = useDispatch();
  const { pages, currentPage, totalCount, sort } = useSelector(
    ({ inventory }) => inventory.vendorManagement
  );

  const vendors = pages[currentPage];
  const totalPages = Math.ceil(totalCount / 25);
  const { orderBy, order } = SORT_STATE[sort] ?? SORT_STATE.VENDOR_ASC;

  const handleSort = (columnId) => {
    let nextOrder = 'asc';
    if (orderBy === columnId && order === 'asc') nextOrder = 'desc';
    dispatch(updateVendorSort({ sort: SORT_ENUM[columnId][nextOrder] }));
  };

  const handlePageChange = (_, page) => {
    if (currentPage === page) return;
    dispatch(listVendorsByPage({ page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Grid component={Paper} sx={{ width: '100%', overflow: 'hidden' }} container direction='column'>
      <Grid item>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow sx={{ backgroundColor: PRIMARY_DARK }}>
                {COLUMNS.map(({ id, label, sortable }) => (
                  <TableCell
                    key={id}
                    sx={headCellSx}
                    sortDirection={orderBy === id ? order : false}
                  >
                    {sortable ? (
                      <TableSortLabel
                        active={orderBy === id}
                        direction={orderBy === id ? order : 'asc'}
                        onClick={() => handleSort(id)}
                        sx={{
                          color: '#fff !important',
                          '& .MuiTableSortLabel-icon': { color: '#fff !important' },
                        }}
                      >
                        {label}
                      </TableSortLabel>
                    ) : (
                      label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {!isNil(vendors) &&
                vendors.map(({ vendorId, vendor, createdAt }) => (
                  <TableRow key={vendorId} hover>
                    <TableCell>{vendor}</TableCell>
                    <TableCell
                      sx={{
                        fontFamily: 'monospace',
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                      }}
                    >
                      {vendorId}
                    </TableCell>
                    <TableCell>
                      {createdAt ? format(new Date(createdAt), 'MM/dd/yyyy hh:mm a') : '—'}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>

            {!isNil(vendors) && totalPages > 1 && (
              <TableFooter>
                <TableRow sx={{ backgroundColor: PRIMARY_DARK }}>
                  <TableCell colSpan={COLUMN_COUNT} align='right' sx={{ borderBottom: 'none' }}>
                    <Pagination
                      sx={{
                        justifyContent: 'flex-end',
                        display: 'flex',
                        '& .MuiPaginationItem-root': { color: '#fff' },
                      }}
                      count={totalPages}
                      page={currentPage}
                      size='small'
                      onChange={handlePageChange}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      {isNil(vendors) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {!isNil(vendors) && isEmpty(vendors) && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}
    </Grid>
  );
};

export default VendorManagementTable;
